import React, { useState, useMemo } from 'react';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import _, { Dictionary } from 'lodash';
import * as types from 'src/domains/query/types';
import { conf, Schema, Resource, Field, getAvailAssoc } from 'src/config';
import { isFieldAllowed } from 'src/auth';

export interface AttrPath {
    resource: Resource
    field: Field
    path: string
}

export type Scope = {
    path: AttrPath,
    id: string,
}

export interface AttrStackItem {
    attr: string
    resource: Resource
}


const schema: Schema = conf.getSchema();


const AttrSelectorProvider: React.FC<{
    children: (props: Scope) => React.ReactElement,
    listConf: types.ListConf,
    hidden: boolean
}> = ({
    children,
    listConf,
    hidden
}) => {
        const { resourceName } = listConf;
        const resource: Resource = schema.resources[resourceName];

        const [stack, setStack] = useState<AttrStackItem[]>([{ attr: '', resource }]);

        const availAttrsPaths: AttrPath[] = useMemo(
            () => {
                const parents = _.tail(stack).map(item => item.attr);
                const lastResource: Resource = _.last(stack)!.resource;
                return _.entries(lastResource.fields)
                    .filter(entry => isFieldAllowed(entry[0]) && (!entry[1].hidden || hidden))
                    .map(([key, field]) => ({
                        resource: lastResource,
                        field,
                        path: [...parents, field.name].join('.')
                    }));
            },
            [stack]
        );

        const availAssoc: Dictionary<Resource> = useMemo<Dictionary<Resource>>(
            () => getAvailAssoc(_.last(stack)!.resource.name, hidden),
            [stack]
        );

        const handlePush = (attr: string, resource: Resource) => {
            setStack([...stack, { attr, resource }]);
        }

        const handlePop = () => {
            setStack(_.initial(stack));
        }

        return <>
            <List dense sx={{ width: '100%', bgcolor: 'background.paper' }}>
                {
                    (stack.length > 1) && <ListItem
                        key={'..'}
                        disablePadding
                    >
                        <ListItemButton onClick={() => handlePop()}>
                            <NavigateBeforeIcon />
                            <ListItemText id={'..'} primary={`back`} />
                        </ListItemButton>
                    </ListItem>
                }

                {
                    availAttrsPaths.map(path => {
                        const id = `checkbox-list-secondary-label-${resource.name}-${path.path}`;

                        return (
                            children({
                                path,
                                id,
                            })
                        );
                    })
                }

                {
                    _.entries(availAssoc).map(([attr, resource]) => {
                        const labelId = `checkbox-list-secondary-label-${resource.name}-${attr}`;

                        return (
                            <ListItem
                                key={attr}
                                secondaryAction={
                                    <NavigateNextIcon />
                                }
                                disablePadding
                            >
                                <ListItemButton onClick={() => handlePush(attr, resource)}>
                                    <ListItemText id={labelId} primary={`${attr}`} />
                                </ListItemButton>
                            </ListItem>
                        );
                    })
                }
            </List>
        </>
    }

export default AttrSelectorProvider;
